// Skills meter dot-grid animation
// Animates `.sg-dot` elements inside each `.skill-meter[data-max-fill]`.
//
// Three behaviors:
//   1. Static start   — bars appear immediately at their t=0 fill levels (staggered
//                        by per-meter phase); no entrance sweep.
//   2. Idle breathing — dual-frequency oscillator (primary + golden-ratio harmonic)
//                        drives the fill edge after START_DELAY_MS; each meter has a
//                        unique phase and speed so they never lock in sync.
//   3. Hover fill     — cursor X sets the fill boundary directly. On leave, the
//                        boundary lerps from the cursor position back to the oscillator
//                        (no jump). CSS transition disabled during hover and return;
//                        re-enabled once settled.
//
// All dot visibility is driven through `currentFill` per meter — a single value that
// the tick loop moves between hover position and breathe boundary as needed.

const GRID_COLS = 32; // must match grid-template-columns count in Webflow

// ── Animation start delay ────────────────────────────────────────────────────
const START_DELAY_MS = 2000; // ms of static display before breathing begins

// ── Idle breathing ──────────────────────────────────────────────────────────
const BREATHE_AMPLITUDE = 0.14;  // fill edge oscillates ±14% (~4–5 columns)
const BREATHE_SPEED = 0.9;       // rad/s (~7s full cycle)
// Unique starting phase per meter — prevents lockstep between bars.
const METER_PHASES = [0, 1.3, 2.7, 0.9];
// Speed multiplier per meter — meters drift through all phase relationships over time.
const METER_SPEEDS = [1.0, 0.83, 1.17, 0.71];
// Golden ratio secondary harmonic — irrational ratio means waveform never repeats.
const PHI = 1.6180339887;

// ── Hover return ─────────────────────────────────────────────────────────────
// Rate at which currentFill lerps toward hoverColNorm while hovering (per second).
const HOVER_RATE = 2;
// Rate at which currentFill lerps back toward breathe after hover ends (per second).
const RETURN_RATE = 2;
// Once within this fraction of meter width, currentFill snaps to breathe and
// CSS transition is re-enabled for normal breathing crossfades.
const RETURN_SNAP_THRESHOLD = 1 / GRID_COLS;

interface MeterState {
  el: HTMLElement;
  dots: HTMLElement[];
  maxFill: number;
  isHovering: boolean;
  isReturning: boolean;   // true while lerping back to breathe after hover
  hoverColNorm: number;   // 0–1 cursor X within the meter
  currentFill: number;    // actual fill boundary used for dot visibility
  prevOpacity: number[];  // last-written opacity per dot; avoids redundant DOM writes
}

function computeBreathe(maxFill: number, mi: number, t: number): number {
  const phase = METER_PHASES[mi] ?? 0;
  const speed = BREATHE_SPEED * (METER_SPEEDS[mi] ?? 1);
  return (
    maxFill
    + Math.sin(t * speed + phase) * BREATHE_AMPLITUDE
    + Math.sin(t * speed * PHI + phase) * (BREATHE_AMPLITUDE * 0.4)
  );
}

export function initSkillsGrid(): void {
  const meterEls = Array.from(
    document.querySelectorAll<HTMLElement>(".skill-meter[data-max-fill]")
  );
  if (meterEls.length === 0) return;

  const states: MeterState[] = meterEls.map((el, mi) => {
    const maxFill = parseFloat(el.dataset.maxFill ?? "1");
    const dots = Array.from(el.querySelectorAll<HTMLElement>(".sg-dot"));
    const prevOpacity = new Array<number>(dots.length).fill(-1);

    // Static initial render at t=0 — no transition, instant appearance.
    const breathe0 = computeBreathe(maxFill, mi, 0);
    dots.forEach((dot, i) => {
      const colNorm = GRID_COLS > 1 ? (i % GRID_COLS) / (GRID_COLS - 1) : 0;
      const op = colNorm <= breathe0 ? 1 : 0;
      dot.style.transition = "none";
      dot.style.opacity = String(op);
      prevOpacity[i] = op;
    });

    const state: MeterState = {
      el, dots, maxFill,
      isHovering: false,
      isReturning: false,
      hoverColNorm: 0.5,
      currentFill: breathe0,
      prevOpacity,
    };

    el.addEventListener("mousemove", (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      state.hoverColNorm = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
      if (!state.isHovering) {
        state.isHovering = true;
        state.isReturning = false;
        // Disable transition — fill boundary tracks cursor without lag
        dots.forEach((d) => { d.style.transition = "none"; });
      }
    });

    el.addEventListener("mouseleave", () => {
      state.isHovering = false;
      state.isReturning = true;
      // Leave transition disabled — the lerp provides visual smoothness.
      // CSS transition will be re-enabled once currentFill settles to breathe.
    });

    return state;
  });

  setTimeout(() => {
    const startTime = performance.now();
    let prevNow = startTime;

    function tick(now: number): void {
      const dt = Math.min((now - prevNow) / 1000, 0.1);
      prevNow = now;
      const t = (now - startTime) / 1000;

      for (let mi = 0; mi < states.length; mi++) {
        const state = states[mi]!;
        const { dots, maxFill, isHovering, isReturning, hoverColNorm, prevOpacity } = state;

        const breathe = computeBreathe(maxFill, mi, t);

        if (isHovering) {
          // Lerp toward cursor — same smooth feel as the return
          state.currentFill += (hoverColNorm - state.currentFill) * Math.min(1, dt * HOVER_RATE);
        } else if (isReturning) {
          // Lerp currentFill toward breathe
          state.currentFill += (breathe - state.currentFill) * Math.min(1, dt * RETURN_RATE);
          if (Math.abs(state.currentFill - breathe) < RETURN_SNAP_THRESHOLD) {
            state.currentFill = breathe;
            state.isReturning = false;
            // Settled — re-enable CSS transition for normal breathing crossfades
            dots.forEach((d) => { d.style.transition = ""; });
          }
        } else {
          // Normal idle — follow breathe directly
          state.currentFill = breathe;
        }

        for (let i = 0; i < dots.length; i++) {
          const colNorm = GRID_COLS > 1 ? (i % GRID_COLS) / (GRID_COLS - 1) : 0;
          const targetOpacity = colNorm <= state.currentFill ? 1 : 0;
          const rounded = Math.round(targetOpacity * 100) / 100;
          if (rounded !== prevOpacity[i]) {
            dots[i]!.style.opacity = String(rounded);
            prevOpacity[i] = rounded;
          }
        }
      }

      requestAnimationFrame(tick);
    }

    // Re-enable transitions before animation begins so breathing crossfades animate
    states.forEach(({ dots }) => dots.forEach((d) => { d.style.transition = ""; }));
    requestAnimationFrame(tick);
  }, START_DELAY_MS);
}
